import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  Bpp,
  BppApiResponse,
  DetailBpp,
  DetailInstitution,
  DetailPosluhdes,
  Institution,
  InstitutionApiResponse,
  Posluhdes,
  PosluhdesApiResponse
} from './types'

import { tables } from './tables'
import { mapToBpp, mapToInstitution, mapToPosluhdes } from './types'

const EXT_REQUEST_TIMEOUT_MS = 30_000
const EXT_QUERY_RETRY = 3

const EXT_INSTITUTIONS = (key?: string | Record<string, any>) =>
  ['ext_institutions', key] as const

interface PaginationParams {
  page?: number
  per_page?: number
}

const extGet = <T>(url: string, config?: { params?: Record<string, unknown> }) =>
  api.get<T>(url, { timeout: EXT_REQUEST_TIMEOUT_MS, ...config })

const getNational = ({
  page,
  per_page
}: PaginationParams): ServiceQueryOptionType<
  ServiceGetAllResponse<Institution[]>
> => ({
  retry: EXT_QUERY_RETRY,
  queryKey: EXT_INSTITUTIONS({ type: 'national', page, per_page }),
  queryFn: async (): Promise<ServiceGetAllResponse<Institution[]>> => {
    try {
      const res = await extGet<
        ServiceGetAllResponse<InstitutionApiResponse[]>
      >('/report/kelembagaan/klb', {
        params: {
          page,
          per_page
        }
      })

      return {
        message: res.data.message,
        data: (res.data.data ?? []).map(mapToInstitution),
        meta: res.data.meta
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getProvince = ({
  page,
  per_page,
  province_code
}: PaginationParams & { province_code: string }): ServiceQueryOptionType<
  ServiceGetAllResponse<Institution[]>
> => ({
  retry: EXT_QUERY_RETRY,
  queryKey: EXT_INSTITUTIONS({
    type: 'province',
    page,
    per_page,
    province_code
  }),
  queryFn: async (): Promise<ServiceGetAllResponse<Institution[]>> => {
    try {
      const res = await extGet<
        ServiceGetAllResponse<InstitutionApiResponse[]>
      >(`/report/kelembagaan/klb/province/${province_code}`, {
        params: {
          page,
          per_page
        }
      })

      return {
        message: res.data.message,
        data: (res.data.data ?? []).map(mapToInstitution),
        meta: res.data.meta
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getKotaKab = ({
  page,
  per_page,
  province_code,
  city_code
}: PaginationParams & {
  province_code: string
  city_code: string
}): ServiceQueryOptionType<ServiceGetAllResponse<Bpp[]>> => ({
  retry: EXT_QUERY_RETRY,
  queryKey: EXT_INSTITUTIONS({
    type: 'city',
    page,
    per_page,
    province_code,
    city_code
  }),
  queryFn: async (): Promise<ServiceGetAllResponse<Bpp[]>> => {
    try {
      const res = await extGet<ServiceGetAllResponse<BppApiResponse[]>>(
        `/report/kelembagaan/klb/province/${province_code}/city/${city_code}`,
        {
          params: {
            page,
            per_page
          }
        }
      )

      return {
        message: res.data.message,
        data: (res.data.data ?? []).map(mapToBpp),
        meta: res.data.meta
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getDistrict = ({
  page,
  per_page,
  province_code,
  city_code,
  district_code
}: PaginationParams & {
  province_code: string
  city_code: string
  district_code: string
}): ServiceQueryOptionType<ServiceGetAllResponse<Posluhdes[]>> => ({
  retry: EXT_QUERY_RETRY,
  queryKey: EXT_INSTITUTIONS({
    type: 'district',
    page,
    per_page,
    province_code,
    city_code,
    district_code
  }),
  queryFn: async (): Promise<ServiceGetAllResponse<Posluhdes[]>> => {
    try {
      const res = await extGet<ServiceGetAllResponse<PosluhdesApiResponse[]>>(
        `/report/kelembagaan/klb/province/${province_code}/city/${city_code}/district/${district_code}`,
        {
          params: {
            page,
            per_page
          }
        }
      )

      return {
        message: res.data.message,
        data: (res.data.data ?? []).map(mapToPosluhdes),
        meta: res.data.meta
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const EXT_INSTITUTIONS_DETAIL = (key?: string | Record<string, any>) =>
  ['ext_institutions_detail', key] as const

const getDetailBpp = ({
  id,
  province_code,
  city_code
}: {
  id: string
  province_code?: string
  city_code?: string
}): ServiceQueryOptionType<ServiceGetByIdResponse<DetailBpp>> => ({
  queryKey: EXT_INSTITUTIONS_DETAIL({
    type: 'bpp',
    id,
    province_code,
    city_code
  }),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetByIdResponse<DetailBpp>>(
        `/report/kelembagaan/klb/province/${province_code}/city/${city_code}/${id}`
      )

      return {
        message: res.data.message,
        data: res.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getDetailPosluhdes = ({
  id,
  province_code,
  city_code,
  district_code
}: {
  id: string
  province_code?: string
  city_code?: string
  district_code?: string
}): ServiceQueryOptionType<ServiceGetByIdResponse<DetailPosluhdes>> => ({
  queryKey: EXT_INSTITUTIONS_DETAIL({
    type: 'posluhdes',
    id,
    province_code,
    city_code,
    district_code
  }),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetByIdResponse<DetailPosluhdes>>(
        `/report/kelembagaan/klb/province/${province_code}/city/${city_code}/district/${district_code}/${id}`
      )

      return {
        message: res.data.message,
        data: res.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getDetailProvince = ({
  id
}: {
  id: string
}): ServiceQueryOptionType<ServiceGetByIdResponse<DetailInstitution>> => ({
  queryKey: EXT_INSTITUTIONS_DETAIL({
    type: 'province',
    id
  }),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetByIdResponse<InstitutionApiResponse>>(
        `/report/kelembagaan/klb/${id}`
      )

      if (!res.data.data) {
        throw new Error('Data not found')
      }

      return {
        message: res.data.message,
        data: mapToInstitution(res.data.data)
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getDetailCity = ({
  id
}: {
  id: string
}): ServiceQueryOptionType<ServiceGetByIdResponse<DetailInstitution>> => ({
  queryKey: EXT_INSTITUTIONS_DETAIL({
    type: 'city',
    id
  }),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetByIdResponse<InstitutionApiResponse>>(
        `/report/kelembagaan/petani/kota/${id}`
      )

      if (!res.data.data) {
        throw new Error('Data not found')
      }

      return {
        message: res.data.message,
        data: mapToInstitution(res.data.data)
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

// Dead code: digantikan oleh tables.getPenyuluhCity
// const getCityPenyuluh = ({
//   id
// }: {
//   id: string
// }): ServiceQueryOptionType<ServiceGetByIdResponse<{
//   table_data: Array<{
//     id: string
//     name: string
//     type_extension_worker: string
//   }>
//   table_meta: {
//     current_page: number
//     last_page: number
//     per_page: number
//     total: number
//   }
// }>> => ({
//   queryKey: EXT_INSTITUTIONS_DETAIL({
//     type: 'city-penyuluh',
//     id
//   }),
//   queryFn: async () => {
//     try {
//       const res = await api.get<ServiceGetByIdResponse<{
//         table_data: Array<{
//           id: string
//           name: string
//           type_extension_worker: string
//         }>
//         table_meta: {
//           current_page: number
//           last_page: number
//           per_page: number
//           total: number
//         }
//       }>>(
//         `/report/ketenagaan/penyuluh/kota/${id}`
//       )
//
//       if (!res.data.data) {
//         throw new Error('Data not found')
//       }
//
//       return {
//         message: res.data.message,
//         data: res.data.data
//       }
//     } catch (error) {
//       throw handleAxiosError(error)
//     }
//   }
// })

export const extInstitutions = {
  getNational,
  getProvince,
  getKotaKab,
  getDistrict,
  detail: {
    getProvince: getDetailProvince,
    getCity: getDetailCity,
    // getCityPenyuluh: getCityPenyuluh, // dead code: digantikan oleh tables.getPenyuluhCity
    getBpp: getDetailBpp,
    getPosluhdes: getDetailPosluhdes
  },
  tables
}
